import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { authSlice } from './slices/authSlice';
import { cartSlice } from './slices/cartSlice';
import { uiSlice } from './slices/uiSlice';
import { storeSlice } from './slices/storeSlice';
import { reportFilterSlice } from './slices/reportFilterSlice';
import { reportUISlice } from './slices/reportUISlice';
import { multiOutletContextSlice } from './slices/multiOutletContextSlice';
import { settingsSlice } from './slices/settingsSlice';
import { auditSlice } from './slices/auditSlice';
import { reportMiddleware } from './middleware/reportFilterPersistence';
import { api } from './api/baseApi';

// Separate API instances (using createApi)
import { inventoryApi } from './api/inventoryApi';
import { categoryApi } from './api/categoryApi';
import { subcategoryApi } from './api/subcategoryApi';
import { customerApi } from './api/customerApi';
import { userApi } from './api/userApi';
import { cashDrawerApi } from './api/cashDrawerApi';
import { paymentApi } from './api/paymentApi';
import { storeApi } from './api/storeApi';
import { outletApi } from './api/outletApi';
import { userAssignmentApi } from './api/userAssignmentApi';
import { subscriptionApi } from './api/subscriptionApi';
import { stockTransferApi } from './api/stockTransferApi';
import { multiOutletStockApi } from './api/multiOutletStockApi';
import { unitApi } from './api/unitApi';
import { bankAccountApi } from './api/bankAccountApi';
import { etalaseApi } from './api/etalaseApi';
import { distributorApi } from './api/distributorApi';
import { stockAuditApi } from './api/stockAuditApi';
import { purchaseOrderApi } from './api/purchaseOrderApi';
// Injected API endpoints (using api.injectEndpoints) - just import to inject
import './api/authApi';
import './api/auditApi';
import './api/dashboardApi';
import './api/posApi';
import './api/transactionApi';
import './api/expenseApi';
import './api/reportApi';
import './api/settingsApi';
import './api/outletCashApi';
import './api/outletDrawerApi';
import './api/roleTemplateApi';

export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    cart: cartSlice.reducer,
    ui: uiSlice.reducer,
    store: storeSlice.reducer,
    reportFilter: reportFilterSlice.reducer,
    reportUI: reportUISlice.reducer,
    multiOutletContext: multiOutletContextSlice.reducer,
    settings: settingsSlice.reducer,
    audit: auditSlice.reducer,
    [api.reducerPath]: api.reducer,
    [inventoryApi.reducerPath]: inventoryApi.reducer,
    [categoryApi.reducerPath]: categoryApi.reducer,
    [subcategoryApi.reducerPath]: subcategoryApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [cashDrawerApi.reducerPath]: cashDrawerApi.reducer,
    [paymentApi.reducerPath]: paymentApi.reducer,
    [storeApi.reducerPath]: storeApi.reducer,
    [outletApi.reducerPath]: outletApi.reducer,
    [userAssignmentApi.reducerPath]: userAssignmentApi.reducer,
    [subscriptionApi.reducerPath]: subscriptionApi.reducer,
    [stockTransferApi.reducerPath]: stockTransferApi.reducer,
    [multiOutletStockApi.reducerPath]: multiOutletStockApi.reducer,
    [unitApi.reducerPath]: unitApi.reducer,
    [bankAccountApi.reducerPath]: bankAccountApi.reducer,
    [etalaseApi.reducerPath]: etalaseApi.reducer,
    [distributorApi.reducerPath]: distributorApi.reducer,
    [stockAuditApi.reducerPath]: stockAuditApi.reducer,
    [purchaseOrderApi.reducerPath]: purchaseOrderApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          api.util.resetApiState.type,
          inventoryApi.util.resetApiState.type,
          categoryApi.util.resetApiState.type,
          subcategoryApi.util.resetApiState.type,
          customerApi.util.resetApiState.type,
          userApi.util.resetApiState.type,
          cashDrawerApi.util.resetApiState.type,
          paymentApi.util.resetApiState.type,
          storeApi.util.resetApiState.type,
          outletApi.util.resetApiState.type,
          userAssignmentApi.util.resetApiState.type,
          subscriptionApi.util.resetApiState.type,
          stockTransferApi.util.resetApiState.type,
          multiOutletStockApi.util.resetApiState.type,
          unitApi.util.resetApiState.type,
          bankAccountApi.util.resetApiState.type,
          etalaseApi.util.resetApiState.type,
          distributorApi.util.resetApiState.type,
          stockAuditApi.util.resetApiState.type,
          purchaseOrderApi.util.resetApiState.type,
        ],
      }
    }).concat(
      api.middleware,
      inventoryApi.middleware,
      categoryApi.middleware,
      subcategoryApi.middleware,
      customerApi.middleware,
      userApi.middleware,
      cashDrawerApi.middleware,
      paymentApi.middleware,
      storeApi.middleware,
      outletApi.middleware,
      userAssignmentApi.middleware,
      subscriptionApi.middleware,
      stockTransferApi.middleware,
      multiOutletStockApi.middleware,
      unitApi.middleware,
      bankAccountApi.middleware,
      etalaseApi.middleware,
      distributorApi.middleware,
      stockAuditApi.middleware,
      purchaseOrderApi.middleware,
      ...reportMiddleware
    ),
  devTools: process.env.NODE_ENV !== 'production',
});

// Enable listener behavior for the store
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
